import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { STAGE_PRODUCTION, CAN_DR_CREATE, CAN_APPROVE, CAN_DR_REOPEN } from "@/lib/constants";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Plus, FileText, ImagePlus, Check, X, Send, RotateCcw } from "lucide-react";

const DR_BADGE = { Draft: "bg-slate-100 text-slate-700", Diajukan: "bg-blue-50 text-blue-700", Disetujui: "bg-emerald-50 text-emerald-700", Ditolak: "bg-red-50 text-red-700" };
const emptyForm = { project_id: "", stage: STAGE_PRODUCTION[0], description: "", progress: 0, kendala: "", next_plan: "" };

export default function DailyReports() {
  const { user } = useAuth();
  const canCreate = user && CAN_DR_CREATE.includes(user.role);
  const canApprove = user && CAN_APPROVE.includes(user.role);
  const canReopen = user && CAN_DR_REOPEN.includes(user.role);
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") || "today";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ros, setRos] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/daily-reports", { params: { scope } }); setList(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const openCreate = async () => { const { data } = await api.get("/repair-orders"); setRos(data); setForm(emptyForm); setCurrent(null); setCreateOpen(true); };
  const saveDraft = async () => {
    if (!form.project_id || !form.description) { toast.error("Pilih unit RO dan isi uraian pekerjaan."); return; }
    setBusy(true);
    try { const { data } = await api.post("/daily-reports", { ...form, progress: Number(form.progress) || 0, status: "Draft" }); setCurrent(data); toast.success("Draft laporan dibuat. Tambahkan foto lalu ajukan."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const uploadPhoto = async (file) => {
    if (!current) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("caption", caption); const { data } = await api.post(`/daily-reports/${current.id}/photos`, fd); setCurrent((c) => ({ ...c, photos: [...(c.photos || []), data] })); setCaption(""); toast.success("Foto ditambahkan"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const submitReport = async () => {
    setBusy(true);
    try { await api.post(`/daily-reports/${current.id}/submit`); toast.success("Laporan diajukan untuk persetujuan"); setCreateOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approve = async (r) => { try { await api.post(`/daily-reports/${r.id}/approve`); toast.success("Laporan disetujui"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const reject = async (r) => { const reason = window.prompt("Alasan penolakan (wajib):"); if (!reason) return; try { await api.post(`/daily-reports/${r.id}/revise`, { reason }); toast.success("Laporan ditolak"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const reopen = async (r) => { if (!window.confirm("Buka kembali laporan ini menjadi Draft? Tercatat di Audit History.")) return; try { await api.post(`/daily-reports/${r.id}/reopen`); toast.success("Laporan dibuka kembali"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const tabs = [["today", "Laporan Hari Ini"], ["pending", "Menunggu Persetujuan"], ["history", "Riwayat Laporan"]];
  const imgUrl = (p) => `${API}/files/${p}`;

  return (
    <div className="space-y-4" data-testid="daily-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Daily Report</h1><p className="text-sm text-muted-foreground">{list.length} laporan</p></div>
        {canCreate && <Button onClick={openCreate} data-testid="new-report-btn" className="bg-primary hover:bg-red-700"><Plus size={16} className="mr-1.5" /> Buat Laporan</Button>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([v, l]) => (
          <button key={v} data-testid={`dr-tab-${v}`} onClick={() => setParams({ scope: v })}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${scope === v ? "bg-navy text-white border-navy" : "bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><FileText className="mx-auto mb-2 opacity-40" /> Tidak ada laporan.</Card> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {list.map((r) => (
              <Card key={r.id} data-testid={`report-card-${r.id}`} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono-k3 text-xs font-semibold text-emerald-700">{r.ro_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${DR_BADGE[r.status]}`}>{r.status}</span>
                </div>
                <div className="text-sm font-medium">{r.plate} · {r.stage} · {r.progress}%</div>
                <div className="text-xs text-muted-foreground">{r.reporter_name} · {fmtDateTime(r.created_at)}</div>
                <div className="text-xs">{r.description}</div>
                {r.kendala && <div className="text-xs text-red-600">Kendala: {r.kendala}</div>}
                {r.next_plan && <div className="text-xs text-slate-600">Rencana: {r.next_plan}</div>}
                {r.review_reason && <div className="text-xs text-amber-700">Alasan penolakan: {r.review_reason}</div>}
                {r.photos?.length > 0 && (
                  <div className="flex gap-1.5 overflow-x-auto">
                    {r.photos.map((ph) => <img key={ph.id} src={imgUrl(ph.storage_path)} alt={ph.caption} title={ph.caption} className="h-16 w-16 object-cover rounded border shrink-0" />)}
                  </div>
                )}
                <div className="flex gap-2 pt-1 flex-wrap">
                  {canApprove && r.status === "Diajukan" && (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid={`approve-${r.id}`} onClick={() => approve(r)}><Check size={14} className="mr-1" /> Setujui</Button>
                      <Button size="sm" variant="outline" className="h-9 text-red-600" data-testid={`reject-${r.id}`} onClick={() => reject(r)}><X size={14} className="mr-1" /> Tolak</Button>
                    </>
                  )}
                  {canReopen && (r.status === "Disetujui" || r.status === "Ditolak") && (
                    <Button size="sm" variant="outline" className="h-9" data-testid={`reopen-${r.id}`} onClick={() => reopen(r)}><RotateCcw size={14} className="mr-1" /> Buka Kembali</Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Buat Daily Report</DialogTitle><DialogDescription>Lapor progres pekerjaan harian.</DialogDescription></DialogHeader>
          {!current ? (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Unit / Repair Order *</Label>
                <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
                  <SelectTrigger data-testid="dr-select-ro"><SelectValue placeholder="Pilih unit RO" /></SelectTrigger>
                  <SelectContent>{ros.map((r) => <SelectItem key={r.id} value={r.id}>{r.ro_number} · {r.plate}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Tahap Produksi</Label>
                  <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                    <SelectTrigger data-testid="dr-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGE_PRODUCTION.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label>Progres (%)</Label><Input type="number" min="0" max="100" value={form.progress} onChange={(e) => set("progress", e.target.value)} data-testid="dr-progress" /></div>
              </div>
              <div className="space-y-1"><Label>Uraian Pekerjaan Hari Ini *</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="dr-description" /></div>
              <div className="space-y-1"><Label>Kendala</Label><Textarea value={form.kendala} onChange={(e) => set("kendala", e.target.value)} rows={2} data-testid="dr-kendala" /></div>
              <div className="space-y-1"><Label>Rencana Pekerjaan Berikutnya</Label><Textarea value={form.next_plan} onChange={(e) => set("next_plan", e.target.value)} rows={2} data-testid="dr-nextplan" /></div>
              <Button onClick={saveDraft} disabled={busy} data-testid="dr-save-draft" className="w-full bg-primary hover:bg-red-700 h-11">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Draft & Lanjut Foto"}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">Tambahkan foto (min 1, maks 10). {current.photos?.length || 0}/10 foto.</p>
              <div className="space-y-1"><Label>Keterangan Foto</Label><Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="cth: hasil dempul pintu kanan" data-testid="dr-caption" /></div>
              <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-slate-50" data-testid="dr-photo-label">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Ambil / Pilih Foto
                <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="dr-photo-input" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </label>
              {current.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {current.photos.map((ph) => (
                    <div key={ph.id} className="relative"><img src={imgUrl(ph.storage_path)} alt="" className="h-20 w-full object-cover rounded border" />
                      {ph.caption && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 truncate">{ph.caption}</span>}</div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setCreateOpen(false); load(); }}>Simpan sbg Draft</Button>
                <Button onClick={submitReport} disabled={busy || !(current.photos?.length)} data-testid="dr-submit" className="bg-primary hover:bg-red-700"><Send size={14} className="mr-1" /> Ajukan untuk Persetujuan</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
